'use client';

import { FormControlLabel, Stack, Switch, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import type { Exhibition, RelatedInfo, UploadFile } from "@/app/lib/generated/graphql-types";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import RelatedInfosView from "@/app/features/shared/related-infos/ui/RelatedInfos.public";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";
import { categorizeSpecialExhibitions } from "@/app/features/sites/special-exhibitions/types/categories";
import { useSiteSpecialExhibitionsEditor } from "@/app/features/sites/special-exhibitions/contexts/SiteSpecialExhibitionsEditorContext";
import SpecialExhibitionItemEditorProvider, {
    useSpecialExhibitionItemEditor,
} from "@/app/features/sites/special-exhibitions/contexts/SpecialExhibitionItemEditorContext";
import {
    SpecialExhibitionCardFrame,
    SpecialExhibitionImageFrame,
    SpecialExhibitionsContentFrame,
    SpecialExhibitionsGridFrame,
    SpecialExhibitionsIntroFrame,
    SpecialExhibitionsRootFrame,
    SpecialExhibitionsSectionFrame,
} from "@/app/features/sites/special-exhibitions/ui/shared/SpecialExhibitionsFrames";

interface SpecialExhibitionsEditableProps {
    exhibitions: Exhibition[];
    today: string;
}

export default function SpecialExhibitionsEditable({
    exhibitions,
    today,
}: SpecialExhibitionsEditableProps) {
    const { editable, updateEditable, errorForField } = useSiteSpecialExhibitionsEditor();
    const { current, future, past } = categorizeSpecialExhibitions(exhibitions, today);
    const highlights = current.filter((exhibition) => exhibition.isHighlight);
    const otherCurrent = current.filter((exhibition) => !exhibition.isHighlight);

    if (!editable) return <LoadingSpinner />;

    const titleError = errorForField?.("intro.title") || undefined;
    const subtitleError = errorForField?.("intro.subtitle") || undefined;

    return (
        <SpecialExhibitionsRootFrame>
            <SpecialExhibitionsIntroFrame>
                <SlideshowIntroV2
                    title={editable.intro.title}
                    subtitle={editable.intro.subtitle}
                    images={[editable.intro.image]}
                    linkContentToAnchor={false}
                    startChildren={(
                        <EditMediaButtonOverlay
                            layout="inline"
                            onImageChange={(image) => {
                                if (image) updateEditable({ intro: { image } });
                            }}
                        />
                    )}
                    content={(
                        <Stack spacing={1} alignItems="center">
                            <EditableTypographyV2
                                label="Titel"
                                required
                                component="h1"
                                variant="h1"
                                value={editable.intro.title}
                                onChange={(title) => updateEditable({ intro: { title } })}
                                error={Boolean(titleError)}
                                helperText={titleError}
                            />
                            <EditableTypographyV2
                                label="Untertitel"
                                component="p"
                                variant="subtitle2"
                                value={editable.intro.subtitle ?? ""}
                                onChange={(subtitle) => updateEditable({ intro: { subtitle } })}
                                error={Boolean(subtitleError)}
                                helperText={subtitleError}
                            />
                        </Stack>
                    )}
                />
            </SpecialExhibitionsIntroFrame>

            <SpecialExhibitionsContentFrame>
                {current.length > 0 && (
                    <SpecialExhibitionsSectionFrame
                        sectionId="current-special-exhibitions"
                        heading={`Aktuelle ${highlights.length === 0 ? "Sonderausstellungen" : "Highlights"}`}
                    >
                        <SpecialExhibitionsGridFrame columns={{ xs: 1, md: 12 }} spacing={8}>
                            {highlights.map((exhibition) => (
                                <Grid key={exhibition.documentId} size={{ xs: 1, md: 12 }}>
                                    <SpecialExhibitionItemEditorProvider documentId={exhibition.documentId}>
                                        <HighlightCardEditable />
                                    </SpecialExhibitionItemEditorProvider>
                                </Grid>
                            ))}
                            {otherCurrent.map((exhibition) => (
                                <Grid key={exhibition.documentId} size={{ xs: 1, md: 4 }}>
                                    <SpecialExhibitionItemEditorProvider documentId={exhibition.documentId}>
                                        <StandardCardEditable />
                                    </SpecialExhibitionItemEditorProvider>
                                </Grid>
                            ))}
                        </SpecialExhibitionsGridFrame>
                    </SpecialExhibitionsSectionFrame>
                )}

                {future.length > 0 && (
                    <SpecialExhibitionsSectionFrame
                        sectionId="future-special-exhibitions"
                        heading="Kommende Sonderausstellungen"
                    >
                        <SpecialExhibitionsGridFrame>
                            {future.map((exhibition) => (
                                <Grid key={exhibition.documentId} size={1}>
                                    <SpecialExhibitionItemEditorProvider documentId={exhibition.documentId}>
                                        <StandardCardEditable centered />
                                    </SpecialExhibitionItemEditorProvider>
                                </Grid>
                            ))}
                        </SpecialExhibitionsGridFrame>
                    </SpecialExhibitionsSectionFrame>
                )}

                {past.length > 0 && (
                    <SpecialExhibitionsSectionFrame
                        sectionId="past-special-exhibitions"
                        heading="Vergangene Sonderausstellungen"
                    >
                        <SpecialExhibitionsGridFrame columns={{ xs: 3, md: 6 }} spacing={2}>
                            {past.map((exhibition) => (
                                <Grid key={exhibition.documentId} size={1}>
                                    <SpecialExhibitionItemEditorProvider documentId={exhibition.documentId}>
                                        <PastExhibitionCardEditable />
                                    </SpecialExhibitionItemEditorProvider>
                                </Grid>
                            ))}
                        </SpecialExhibitionsGridFrame>
                    </SpecialExhibitionsSectionFrame>
                )}

                {editable.relatedInfos.length > 0 && (
                    <RelatedInfosView relatedInfos={editable.relatedInfos as RelatedInfo[]} />
                )}
            </SpecialExhibitionsContentFrame>
        </SpecialExhibitionsRootFrame>
    );
}

function HighlightCardEditable() {
    const { editable, updateEditable, errorForField } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <SpecialExhibitionCardFrame>
            <Grid container spacing={8}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <EditablePromoImage />
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Stack spacing={2}>
                        <EditableDateRange />
                        <EditableTypographyV2
                            label="Titel"
                            required
                            component="h3"
                            variant="h2"
                            value={editable.title}
                            onChange={(title) => updateEditable({ title })}
                            error={Boolean(errorForField?.("title"))}
                            helperText={errorForField?.("title") || undefined}
                        />
                        <EditableTypographyV2
                            label="Untertitel"
                            component="p"
                            variant="subtitle2"
                            value={editable.subtitle ?? ""}
                            onChange={(subtitle) => updateEditable({ subtitle })}
                            error={Boolean(errorForField?.("subtitle"))}
                            helperText={errorForField?.("subtitle") || undefined}
                        />
                        <EditableMarkdownV2
                            label="Kurzbeschreibung"
                            required
                            value={editable.excerpt}
                            onChange={(excerpt) => updateEditable({ excerpt })}
                            error={Boolean(errorForField?.("excerpt"))}
                            helperText={errorForField?.("excerpt") || undefined}
                        />
                        <HighlightSwitch />
                    </Stack>
                </Grid>
            </Grid>
        </SpecialExhibitionCardFrame>
    );
}

function StandardCardEditable({ centered = false }: { centered?: boolean }) {
    const { editable, updateEditable, errorForField } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <SpecialExhibitionCardFrame>
            <Stack spacing={2} alignItems={centered ? "center" : "stretch"}>
                <EditablePromoImage />
                <Stack spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
                    <EditableDateRange />
                    <EditableTypographyV2
                        label="Titel"
                        required
                        component="h3"
                        variant="h3"
                        value={editable.title}
                        onChange={(title) => updateEditable({ title })}
                        error={Boolean(errorForField?.("title"))}
                        helperText={errorForField?.("title") || undefined}
                    />
                    <EditableTypographyV2
                        label="Untertitel"
                        component="p"
                        variant="subtitle2"
                        value={editable.subtitle ?? ""}
                        onChange={(subtitle) => updateEditable({ subtitle })}
                        error={Boolean(errorForField?.("subtitle"))}
                        helperText={errorForField?.("subtitle") || undefined}
                    />
                    <HighlightSwitch />
                </Stack>
            </Stack>
        </SpecialExhibitionCardFrame>
    );
}

function EditableDateRange() {
    const { editable, updateEditable, errorForField } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
            <TextField
                label="Startdatum"
                type="date"
                required
                value={editable.startDate}
                onChange={(event) => updateEditable({ startDate: event.target.value })}
                error={Boolean(errorForField?.("startDate"))}
                helperText={errorForField?.("startDate") || undefined}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
            />
            <TextField
                label="Enddatum"
                type="date"
                required
                value={editable.endDate}
                onChange={(event) => updateEditable({ endDate: event.target.value })}
                error={Boolean(errorForField?.("endDate"))}
                helperText={errorForField?.("endDate") || undefined}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
            />
        </Stack>
    );
}

function HighlightSwitch() {
    const { editable, updateEditable } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <FormControlLabel
            control={(
                <Switch
                    checked={editable.isHighlight}
                    onChange={(event) => updateEditable({ isHighlight: event.target.checked })}
                />
            )}
            label="Als Highlight anzeigen"
        />
    );
}

function EditablePromoImage() {
    const { editable, updateEditable } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <SpecialExhibitionImageFrame>
            <MediaView
                src={editable.promoImage ?? PLACEHOLDER_IMAGE}
                fill
                objectFit="cover"
                slots={{
                    root: {
                        sx: {
                            width: "100%",
                            aspectRatio: "3 / 2",
                        },
                    },
                    image: {
                        className: "signature-border-radius",
                        style: {
                            width: "100%",
                            height: "100%",
                        },
                    },
                }}
            />
            <EditMediaButtonOverlay
                onImageChange={(promoImage) => {
                    if (promoImage) updateEditable({ promoImage });
                }}
            />
        </SpecialExhibitionImageFrame>
    );
}

function PastExhibitionCardEditable() {
    const { editable, updateEditable } = useSpecialExhibitionItemEditor();

    if (!editable) return null;

    return (
        <SpecialExhibitionImageFrame>
            <MediaView
                src={editable.flyerImage || editable.promoImage || PLACEHOLDER_IMAGE}
                fill
                objectFit="cover"
                slots={{
                    root: {
                        sx: {
                            width: "100%",
                            aspectRatio: "25 / 35",
                        },
                    },
                    image: {
                        className: "signature-border-radius",
                        style: {
                            width: "100%",
                            height: "100%",
                        },
                    },
                }}
            />
            <EditMediaButtonOverlay
                onImageChange={(flyerImage) => updateEditable({
                    flyerImage: flyerImage as UploadFile | null,
                })}
            />
        </SpecialExhibitionImageFrame>
    );
}
